import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { cn } from '@/utils/cn';
import { useSearchParams } from 'react-router-dom';
import MenuFilters, { type FilterObject as MenuFilterObject } from './MenuFilters';
import MenuFilterDrawer from './MenuFilterDrawer';
import OpenTimingPopup from '@/components/features/OpenTimingPopup';
import MenuItem from '@/components/ui/MenuItem';
import AddToBasket from '@/components/features/AddToBasket';
import MenuCard from './MenuCard';
import { fetchAllMenus, getMenuCategoryIds, getMenuGroups, toMenuCard, MenuOffer } from '@/data/menus';
import TopSeller from '@/components/features/TopSeller';
import { menuPage, openTimings, cartLabels, templateData } from '@/data/content';
import { formatPortionLabel, getMenuCategories, menuItems } from '@/data/foods';
import { useCart, buildCartOrders } from '@/Context/CartContext';
import { useMenuSSE } from '@/hooks/useMenuSSE';
import { STORAGE_EVENT_DATE } from '@/hooks/useStorageEvent';
import Button from '@/components/ui/Button';
import api from '@/Services/api';
import { type ResolvedSlot, type FoodUpdate, type AvailabilityDay, type FoodItem } from '@/types/menu';
import { useMenuAvailabilityQuery, useMenuSlotQuery } from '@/hooks/useMenuQueries';

interface FilterableMenuCard {
    id: number;
    category_ids: number[];
    visible: boolean;
    card: ReturnType<typeof toMenuCard>;
}

interface StoredMenuFilter {
    date: string;
}

const resolveDefaultDay = (days: AvailabilityDay[]) => days.find((day) => day.foodCount > 0) ?? days[0] ?? null;

const LoadFoodView: React.FC = () => {
    const { cartItems, setInvalidItemIds, addToCart, toggleCart } = useCart();
    const cartOrders = useMemo(() => buildCartOrders(cartItems), [cartItems]);
    const [searchParams, setSearchParams] = useSearchParams();

    const dateParam = searchParams.get('date');
    const foodIdParam = searchParams.get('food_id');
    const categoriesParam = searchParams.get('categories');
    const oosParam = searchParams.get('oos');

    const derived = useMemo(() => {
        const dateStr = dateParam ?? localStorage.getItem('selectedServiceDate') ?? undefined;
        const categoryIds = categoriesParam ? categoriesParam.split(',').map((id) => id.trim()) : [];
        const includeOutOfStock = oosParam === '1';
        const foodId = foodIdParam ? Number(foodIdParam) : null;
        const selectedDate = dateStr ? new Date(dateStr + 'T00:00:00') : null;

        return { dateStr, categoryIds, includeOutOfStock, foodId, selectedDate };
    }, [dateParam, categoriesParam, oosParam, foodIdParam]);

    const {
        data: availabilityDays = [],
        isSuccess: availabilityReady,
        isLoading: isAvailabilityLoading,
    } = useMenuAvailabilityQuery();

    const resolvedDateToFetch =
        derived.dateStr ?? (availabilityDays.length > 0 ? resolveDefaultDay(availabilityDays)?.date : undefined);

    const { data: slotData, isFetching: isSlotLoading } = useMenuSlotQuery(resolvedDateToFetch, cartOrders);
    const [allMenus, setAllMenus] = useState<MenuOffer[]>([]);

    useEffect(() => {
        fetchAllMenus().then(setAllMenus).catch(() => {});
    }, []);

    const loading = isAvailabilityLoading || isSlotLoading;
    const foodUpdates = slotData?.foodUpdates ?? new Map<number, FoodUpdate>();
    const resolvedSlot = slotData?.slot ?? null;
    const availableMenuIds = slotData?.availableMenuIds ?? [];
    const menuOverlayReady = !!slotData;

    useEffect(() => {
        if (slotData?.invalidCartItemIds) {
            setInvalidItemIds(new Set(slotData.invalidCartItemIds));
        }
    }, [slotData?.invalidCartItemIds, setInvalidItemIds]);

    useMenuSSE(resolvedSlot);

    const categories = useMemo(
        () => getMenuCategories(),
        []
    );
    const foodItems = useMemo(
        () => menuItems as unknown as FoodItem[],
        []
    );
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [showPopup, setShowPopup] = useState(false);

    const saveStoredFilter = (value: StoredMenuFilter | null) => {
        try {
            if (value && value.date) {
                window.localStorage.setItem('selectedServiceDate', value.date);
                window.dispatchEvent(new CustomEvent(STORAGE_EVENT_DATE));
            } else {
                window.localStorage.removeItem('selectedServiceDate');
                window.dispatchEvent(new CustomEvent(STORAGE_EVENT_DATE));
            }
        } catch {
            /* ignore */
        }
    };

    const activeFoodFilter = useMemo(() => {
        if (!derived.foodId) return null;
        const food = menuItems.find((f) => f.id === derived.foodId);
        return { id: derived.foodId, name: food?.name ?? `#${derived.foodId}` };
    }, [derived.foodId]);

    const syncFiltersToUrl = useCallback(
        (filters: {
            date?: string | null;
            categoryIds?: (string | number)[];
            includeOutOfStock?: boolean;
            foodId?: number | null;
        }) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);

                    if (filters.date !== undefined) {
                        if (filters.date) {
                            next.set('date', filters.date);
                        } else {
                            next.delete('date');
                        }
                    }

                    if (filters.categoryIds && filters.categoryIds.length > 0) {
                        next.set('categories', filters.categoryIds.join(','));
                    } else {
                        next.delete('categories');
                    }
                    next.delete('category');

                    if (filters.includeOutOfStock) next.set('oos', '1');
                    else next.delete('oos');

                    next.delete('q');

                    if (filters.foodId) next.set('food_id', String(filters.foodId));
                    else next.delete('food_id');

                    next.delete('period');
                    next.delete('fulfilment');

                    return next;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    const handleDateChange = (date: Date | null) => {
        const dateStr = date
            ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
            : undefined;

        if (dateStr) {
            saveStoredFilter({ date: dateStr });
        } else {
            saveStoredFilter(null);
        }

        syncFiltersToUrl({
            date: dateStr ?? null,
            categoryIds: derived.categoryIds,
            includeOutOfStock: derived.includeOutOfStock,
            foodId: derived.foodId,
        });
    };

    const handleCategoryChange = (ids: (string | number)[]) => {
        syncFiltersToUrl({
            categoryIds: ids,
            includeOutOfStock: derived.includeOutOfStock,
            foodId: derived.foodId,
        });
    };

    const handleResetFilters = useCallback(() => {
        const defaultDay = resolveDefaultDay(availabilityDays);
        const resolvedDate = defaultDay?.date;

        saveStoredFilter(null);

        syncFiltersToUrl({
            date: resolvedDate ?? null,
            categoryIds: [],
            includeOutOfStock: false,
            foodId: null,
        });
    }, [availabilityDays, syncFiltersToUrl]);

    const handleRemoveFoodFilter = () => {
        syncFiltersToUrl({
            categoryIds: derived.categoryIds,
            includeOutOfStock: derived.includeOutOfStock,
            foodId: null,
        });
    };

    const handleIncludeOutOfStockChange = (v: boolean) => {
        syncFiltersToUrl({
            categoryIds: derived.categoryIds,
            includeOutOfStock: v,
            foodId: derived.foodId,
        });
    };

    const handleAddMenuToCart = useCallback(
        (menuId: number, quantity: number = 1) => {
            const menu = allMenus.find((candidate) => candidate.id === menuId);
            if (!menu || !resolvedSlot) return;

            const discount = menu.discount_percentage / 100;
            const instanceId = `menu-${menu.id}-${Date.now()}`;
            const groups = getMenuGroups(menu);
            const totalMenuPrice = Math.round(Number(menu.price) * (1 - discount) * 100) / 100;
            let isFirstItem = true;

            groups.forEach((group) => {
                group.foods.forEach((groupFood) => {
                    const food = menuItems.find((item) => item.id === groupFood.id);
                    if (!food) return;

                    const basePrice = Number(groupFood.price ?? food.price);
                    let itemPrice = Math.round(basePrice * (1 - discount) * 100) / 100;
                    if (Number(menu.price) > 0) {
                        itemPrice = isFirstItem ? totalMenuPrice : 0;
                        isFirstItem = false;
                    }

                    const portionLabel = formatPortionLabel(groupFood.portion ?? food.portion_quantity, (groupFood as unknown as Record<string, unknown>).food_unit as string | undefined ?? 'g');

                    addToCart(
                        {
                            id: food.id,
                            name: food.item_name,
                            item_name: food.item_name,
                            price: itemPrice,
                            weight_percentage: food.weight_percentage,
                            menu_id: menu.id,
                            menu_name: menu.name,
                            menu_instance_id: instanceId,
                            menu_group_name: group.name,
                            portion_label: portionLabel ?? food.portion_label,
                            date: resolvedSlot.date,
                            period: resolvedSlot.period,
                            fulfilment: resolvedSlot.fulfilment,
                        },
                        quantity
                    );
                });
            });

            if (quantity > 0) toggleCart();
        },
        [allMenus, resolvedSlot, addToCart, toggleCart]
    );

    const availableIsoDates = useMemo(() => new Set(availabilityDays.map((day) => day.date)), [availabilityDays]);
    const remainingWeight = resolvedSlot ? 100 - resolvedSlot.slotWeight : 100;

    const normalizedItems = useMemo(() => {
        return foodItems.map((food) => {
            const update = foodUpdates.get(food.id);
            const isReturned = !!update;
            const canBeAddedToCart = menuOverlayReady && (update?.is_available ?? update?.available ?? isReturned);
            const visible = !menuOverlayReady || derived.includeOutOfStock || canBeAddedToCart;

            // Derive dynamic fields from update if available (static data no longer has price)
            let price: number | undefined;
            let portionLabel: string | undefined;

            if (update?.variants && update.variants.length > 0) {
                const standard = update.variants.find((v) => v.type === 'food') || update.variants[0];
                if (standard) {
                    price = standard.price;
                    portionLabel = formatPortionLabel(standard.portion, update.food_unit);
                }
            }

            return {
                ...food,
                isLoading: !menuOverlayReady,
                canBeAddedToCart,
                visible,
                daily_capacity: update?.daily_capacity,
                weight_percentage: update?.weight_percentage,
                preparation_days: update?.preparation_days,
                is_top_seller: !!update?.is_top_seller,
                food_unit: update?.food_unit,
                price,
                portion_label: portionLabel,
                variants: update?.variants,
            };
        });
    }, [foodItems, foodUpdates, menuOverlayReady, derived.includeOutOfStock]);

    const normalizedMenuCards = useMemo<FilterableMenuCard[]>(() => {
        const availableSet = new Set(availableMenuIds);
        const available =
            derived.includeOutOfStock || !menuOverlayReady ? allMenus : allMenus.filter((m) => availableSet.has(m.id));

        return available.map((menu) => ({
            id: menu.id,
            category_ids: getMenuCategoryIds(menu),
            visible: derived.includeOutOfStock || !menuOverlayReady || availableSet.has(menu.id),
            card: toMenuCard(menu),
        }));
    }, [allMenus, availableMenuIds, derived.includeOutOfStock, menuOverlayReady]);

    const filteredItems = useMemo(() => {
        let items = normalizedItems.filter((f) => f.visible);

        // Food filter (frontend-owned)
        if (activeFoodFilter) {
            items = items.filter((f) => f.id === activeFoodFilter.id);
        }

        // Category filter (frontend-owned)
        if (derived.categoryIds.length > 0) {
            const catIds = new Set(derived.categoryIds.map(String));
            items = items.filter((f) => catIds.has(String(f.category_id)));
        }

        return items;
    }, [normalizedItems, activeFoodFilter, derived.categoryIds]);

    const filteredMenuCards = useMemo(() => {
        let items = normalizedMenuCards.filter((menu) => menu.visible);

        if (derived.categoryIds.length > 0) {
            const catIds = new Set(derived.categoryIds.map(String));
            items = items.filter((menu) => menu.category_ids.some((categoryId) => catIds.has(String(categoryId))));
        }

        return items;
    }, [normalizedMenuCards, derived.categoryIds]);

    const totalResultCount = filteredItems.length + filteredMenuCards.length;

    const filterObject: MenuFilterObject = {
        categories: categories.map((c) => ({ id: c.id, category_name: c.name, ficon: c.ficon ?? undefined })),
        activeCategories: derived.categoryIds,
        onSelectCategories: handleCategoryChange,
        selectedDate: derived.selectedDate,
        openTimings,
        availableIsoDates,
        onSelectDate: handleDateChange,
        resolvedSlot,
        includeOutOfStock: derived.includeOutOfStock,
        onIncludeOutOfStockChange: handleIncludeOutOfStockChange,
        activeFoodFilter,
        onRemoveFoodFilter: handleRemoveFoodFilter,
        onOpenFilters: () => {
            setDrawerOpen(true);
        },
        onOpenDatePicker: () => setShowPopup(true),
    };

    const cartSlot =
        derived.dateStr && resolvedSlot
            ? {
                  date: derived.dateStr,
                  period: resolvedSlot.period,
                  fulfilment: resolvedSlot.fulfilment,
                  slotWeight: resolvedSlot.slotWeight,
              }
            : null;

    const hasValidSlot = !!cartSlot;

    return (
        <div className="bg-app-bg min-h-screen">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-20 pb-8 md:pb-12">
                <div className="mb-6 flex justify-start">
                    <Button to="/" size="sm" variant="outline" className="px-6 py-2">
                        ← {templateData.back_home}
                    </Button>
                </div>
                <h1 className="text-4xl md:text-6xl font-black text-app-text mb-4 tracking-tight">
                    {menuPage.page_title}
                </h1>
                <p className="text-base md:text-xl text-app-muted leading-relaxed">{menuPage.description}</p>
            </div>

            <section className="py-4 md:py-8 relative">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {availabilityReady && (
                        <div className="mb-8 md:mb-12">
                            <MenuFilters
                                filterObject={filterObject}
                                isLoading={loading}
                                remainingWeight={remainingWeight}
                                showFilters
                            />
                        </div>
                    )}

                    <div className="flex justify-end mb-4 md:hidden">
                        <Button
                            variant="secondary"
                            onClick={() => setViewMode((v) => (v === 'grid' ? 'list' : 'grid'))}
                            className="!p-2 rounded-xl"
                            aria-label={viewMode === 'grid' ? menuPage.view_list : menuPage.view_grid}
                        >
                            {viewMode === 'grid' ? (
                                <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                >
                                    <line x1="3" y1="6" x2="21" y2="6" />
                                    <line x1="3" y1="12" x2="21" y2="12" />
                                    <line x1="3" y1="18" x2="21" y2="18" />
                                </svg>
                            ) : (
                                <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                >
                                    <rect x="3" y="3" width="7" height="7" />
                                    <rect x="14" y="3" width="7" height="7" />
                                    <rect x="3" y="14" width="7" height="7" />
                                    <rect x="14" y="14" width="7" height="7" />
                                </svg>
                            )}
                        </Button>
                    </div>

                    {filteredMenuCards.length > 0 && (
                        <div className="mb-8">
                            <h2 className="text-lg font-black text-app-text mb-4">{menuPage.menu_section_heading}</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
                                {filteredMenuCards.map((menu) => (
                                    <MenuCard key={menu.id} {...menu.card} onAddToCart={handleAddMenuToCart} />
                                ))}
                            </div>
                        </div>
                    )}

                    <div
                        className={cn(
                            'grid',
                            viewMode === 'grid' ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6 lg:gap-8' : 'grid-cols-1 gap-6 lg:gap-8'
                        )}
                    >
                        {filteredItems.map((item: FoodItem) => (
                            <MenuItem
                                loading={loading}
                                key={item.id}
                                item={{ ...item, item_name: item.name }}
                                selectedDate={derived.selectedDate}
                                isAvailable={true}
                                compact={viewMode === 'grid'}
                                resolvedSlot={cartSlot}
                                renderAddToBasket={(foodId, price, isAvail, maxQty, isCompact) => (
                                    <AddToBasket
                                        compact={isCompact ?? viewMode === 'grid'}
                                        foodId={foodId}
                                        price={price}
                                        isAvailable={isAvail}
                                        maxQuantity={maxQty ?? 99}
                                    />
                                )}
                            />
                        ))}
                    </div>

                    {totalResultCount === 0 && !loading && (
                        <div className="text-center w-full py-24 bg-app-surface rounded-[3rem] border border-app-border shadow-sm">
                            {resolvedSlot && resolvedSlot.slotWeight > 0 ? (
                                <>
                                    <span className="text-5xl block mb-6">🧺</span>
                                    <h3 className="text-2xl font-black text-app-text mb-2">
                                        {cartLabels.weight_cap_title}
                                    </h3>
                                    <p className="text-base text-app-muted max-w-sm mx-auto mb-6">
                                        {cartLabels.weight_cap_slot_full}
                                    </p>
                                    <Button
                                        onClick={() => {
                                            // The query automatically refetches, but user can clear out invalid items via cart UI
                                        }}
                                        className="mt-2 rounded-full font-bold text-sm"
                                    >
                                        {cartLabels.weight_cap_slot_full_cta}
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <span className="text-5xl block mb-6 grayscale opacity-40">🔍</span>
                                    <h3 className="text-2xl font-black text-app-text mb-2">
                                        {menuPage.no_foods_for_filter}
                                    </h3>
                                    <Button
                                        onClick={handleResetFilters}
                                        className="mt-4 rounded-full font-bold text-sm"
                                    >
                                        {menuPage.filter_reset}
                                    </Button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </section>
            <TopSeller />

            {availabilityReady && (
                <MenuFilterDrawer
                    isOpen={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    categories={categories.map((c) => ({
                        id: c.id,
                        category_name: c.name,
                        ficon: c.ficon ?? undefined,
                    }))}
                    activeCategories={derived.categoryIds}
                    onToggleCategory={(id) => {
                        const strId = String(id);
                        const already = derived.categoryIds.some((c) => String(c) === strId);
                        handleCategoryChange(
                            already
                                ? derived.categoryIds.filter((c) => String(c) !== strId)
                                : [...derived.categoryIds, id]
                        );
                    }}
                    includeOutOfStock={derived.includeOutOfStock}
                    onToggleStock={handleIncludeOutOfStockChange}
                    resultCount={totalResultCount}
                    onResetFilters={handleResetFilters}
                />
            )}

            <OpenTimingPopup
                isOpen={showPopup}
                onClose={() => setShowPopup(false)}
                onSelect={(date) => handleDateChange(date)}
                applyLabel="Counter"
                onFetchCount={async (date) => {
                    const { data } = await api.post('/api/menu', {
                        date: date.toISOString().split('T')[0],
                        cartOrders,
                    });
                    const updates = data.food_updates ?? [];
                    return updates.filter((u: any) => u.is_available).length;
                }}
                availableIsoDates={availableIsoDates}
            />
        </div>
    );
};

export default LoadFoodView;
